import { Object, String, Number, Boolean, Static, Null, Undefined, Array, Union, Literal } from 'runtypes';
import { Route } from './utils';
import { StateRecord } from './class';

export const userRoutes = (): { [name: string]: Route } => ({
  signup: {
    method: 'post',
    path: '/user/signup',
    requestBody: SignupRequestRecord,
    responseData: UserResponseRecord,
  },
  signin: {
    method: 'post',
    path: '/user/signin',
    requestBody: SigninRequestRecord,
    responseData: UserResponseRecord,
  },
  signout: {
    method: 'get',
    path: '/user/signout',
    requestBody: Undefined,
    responseData: SignoutResponseRecord,
  },
  user: {
    method: 'get',
    path: '/user',
    requestBody: Undefined,
    responseData: UserResponseRecord,
  },
  updateUser: {
    method: 'patch',
    path: '/user',
    requestBody: UpdateUserRequestRecord,
    responseData: UserResponseRecord,
  },
  getState: {
    method: 'get',
    path: '/user/state',
    requestBody: Undefined,
    responseData: StateRecord,
  },
  setState: {
    method: 'patch',
    path: '/user/state',
    requestBody: StateRecord,
    responseData: StateRecord,
  },
  userKeys: {
    method: 'get',
    path: '/user-keys',
    requestBody: Undefined,
    responseData: UserKeyResponseRecord,
  },
  getForumLogin: {
    method: 'post',
    path: '/get-forum-credentials',
    requestBody: TeacherForumRequestRecord,
    responseData: TeacherForumResponseRecord,
  },
  resetPasswordStart: {
    method: 'post',
    path: '/user/reset-password-start',
    requestBody: ResetPasswordStartRequestRecord,
    responseData: ResetPasswordStartResponseRecord,
  },
  resetPasswordFinish: {
    method: 'post',
    path: '/user/reset-password-finish',
    requestBody: ResetPasswordFinishRequestRecord,
    responseData: ResetPasswordFinishResponseRecord,
  },
  removeUserClasses: {
    method: 'put',
    path: '/user/:userId/remove-classes',
    requestBody: Object({}),
    responseData: RemoveUserClassesResponseRecord,
  },
  // This should only be used for testing
  deleteUser: {
    method: 'delete',
    path: '/user/:userId',
    requestBody: Undefined,
    responseData: DeleteUserResponseRecord,
  },
  authClever: {
    method: 'get',
    path: '/auth/clever',
    requestBody: Undefined,
    responseData: Object({}),
  },
  authCanvas: {
    method: 'get',
    path: '/auth/canvas',
    requestBody: Undefined,
    responseData: Object({}),
  },
  authGoogle: {
    method: 'get',
    path: '/auth/google',
    requestBody: Undefined,
    responseData: Object({}),
  },
  authIbmSam: {
    method: 'get',
    path: '/auth/ibm-sam',
    requestBody: Undefined,
    responseData: Object({}),
  },
  authClassLink: {
    method: 'get',
    path: '/auth/class-link',
    requestBody: Undefined,
    responseData: Object({}),
  },
  authLti: {
    method: 'get',
    path: '/auth/lti',
    requestBody: Undefined,
    responseData: Object({}),
  },
});

// This should match UserRole in types/models.ts
const UserRoleRecord = Union(
  Literal('teacher'),
  Literal('admin'),
  Literal('author'),
  Literal('student'),
  Literal('customerSupport'),
);
// This should match UserAccountStatusRecord in types/models.ts
export const UserAccountStatusRecord = Union(Literal('active'), Literal('disabled'), Literal('pending'));
/*
 * The Django admin dashboard can make this
 * into arbitrary JSON.  The server should fix any
 * incompatible format silently.
 */
export const UserAccountProfileRecord = Object({
  school: String.optional(),
  district: String.optional(),
  profileRole: String.optional(),
  directLoginDisabled: Boolean.optional(),
});

const InstitutionRoleRecord = Object({
  institutionId: Number,
  // This should match InstitutionUserRole in types/models.ts
  roles: Array(Union(Literal('institutionAdmin'), Literal('teacher'), Literal('student'))),
});

export const SignupRequestRecord = Object({
  name: String.or(Null),
  firstName: String.or(Null),
  middleName: String.or(Null),
  lastName: String.or(Null),
  emails: Array(String), // So as not to confuse with users.email
  password: String,
  profile: UserAccountProfileRecord,
});

const UpdateUserRequestRecord = Object({
  profile: UserAccountProfileRecord,
});
export const SigninRequestRecord = Object({
  email: String,
  password: String,
});

export const SignoutResponseRecord = Object({});

export const TeacherForumRequestRecord = Object({});
export const TeacherForumResponseRecord = Object({ username: String, password: String });

// This should match ExternalSource in types/models.ts
export const ExternalSourceRecord = Union(
  Literal('clever'),
  Literal('moodle'),
  Literal('canvas'),
  Literal('google'),
  Literal('oneRoster'),
  Literal('lti'),
  Literal('Pico'),
  Literal('Quest'),
  Literal('Unknown'),
  Literal('Mailer'),
  Literal('PrismsScoreService'),
  Literal('manageXR'),
);

export const UserResponseRecord = Object({
  id: Number,
  status: UserAccountStatusRecord,
  name: String.or(Null),
  firstName: String.or(Null),
  middleName: String.or(Null),
  lastName: String.or(Null),
  roles: Array(UserRoleRecord),
  institutionRoles: Array(InstitutionRoleRecord),
  email: String,
  emails: Array(String),
  externalSource: ExternalSourceRecord.or(Null),
  externalInstance: String.or(Null),
  profile: UserAccountProfileRecord,
});

const UserKeyDataRecord = Object({
  id: Number,
  key: String,
  externalName: String,
  nickName: String.or(Null),
  section: String.or(Null),
  role: String,
});
export const UserKeyResponseRecord = Array(UserKeyDataRecord);

export const ResetPasswordStartRequestRecord = Object({
  email: String,
});

export const ResetPasswordStartResponseRecord = Object({});

export const ResetPasswordFinishRequestRecord = Object({
  token: String,
  password: String,
});

export const ResetPasswordFinishResponseRecord = Object({});
export const DeleteUserResponseRecord = Undefined.or(Null);
export const RemoveUserClassesResponseRecord = Undefined.or(Null);

export type SignupRequest = Static<typeof SignupRequestRecord>;
export type SigninRequest = Static<typeof SigninRequestRecord>;
export type SignoutResponse = Static<typeof SignoutResponseRecord>;
export type UserAccountStatus = Static<typeof UserAccountStatusRecord>;
export type UserResponse = Static<typeof UserResponseRecord>;
export type UpdateUserRequest = Static<typeof UpdateUserRequestRecord>;
export type UserAccountProfile = Static<typeof UserAccountProfileRecord>;
export type ExternalSource = Static<typeof ExternalSourceRecord>;
export type UserKeyResponse = Static<typeof UserKeyResponseRecord>;
export type ResetPasswordStartRequest = Static<typeof ResetPasswordStartRequestRecord>;
export type ResetPasswordStartResponse = Static<typeof ResetPasswordStartResponseRecord>;
export type ResetPasswordFinishRequest = Static<typeof ResetPasswordFinishRequestRecord>;
export type ResetPasswordFinishResponse = Static<typeof ResetPasswordFinishResponseRecord>;
export type TeacherForumResponse = Static<typeof TeacherForumResponseRecord>;
